import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiConfig } from '../config';

const API_BASE = apiConfig.baseUrl;

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('hmh_access_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export interface BirthdayEmployee {
  id: number;
  employee_name: string;
  birthday_month: number;
  birthday_day: number;
  email: string | null;
  entra_id: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BirthdayTicketLog {
  id: number;
  birthday_employee_id: number;
  employee_name: string;
  ticket_code: string | null;
  ticket_id: string | null;
  birthday_date: string;
  email_sent: boolean;
  email_sent_at: string | null;
  email_error: string | null;
  created_at: string;
}

export interface UploadSummary {
  added: number;
  updated: number;
  errors: string[];
  total: number;
  batch_id: string;
}

export interface ProcessSummary {
  processed: number;
  skipped: number;
  date: string;
  results: Array<{
    employee: string;
    ticket_code: string;
    log_id: number;
    has_email: boolean;
  }>;
  email_sent: number;
  email_failed: number;
}

interface EmployeeParams {
  is_active?: boolean;
  month?: number;
  search?: string;
}

interface LogParams {
  birthday_date?: string;
  employee_id?: number;
  email_sent?: boolean;
}

export function useBirthdayEmployees(params?: EmployeeParams) {
  return useQuery({
    queryKey: ['birthday-employees', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.is_active !== undefined) searchParams.append('is_active', String(params.is_active));
      if (params?.month !== undefined) searchParams.append('month', String(params.month));
      if (params?.search) searchParams.append('search', params.search);
      const qs = searchParams.toString();
      const url = `${API_BASE}/admin/birthdays${qs ? '?' + qs : ''}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Failed to fetch birthday employees: ${error}`);
      }
      return res.json() as Promise<BirthdayEmployee[]>;
    }
  });
}

export function useBirthdayTicketLogs(params?: LogParams) {
  return useQuery({
    queryKey: ['birthday-logs', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.birthday_date) searchParams.append('birthday_date', params.birthday_date);
      if (params?.employee_id !== undefined) searchParams.append('employee_id', String(params.employee_id));
      if (params?.email_sent !== undefined) searchParams.append('email_sent', String(params.email_sent));
      const qs = searchParams.toString();
      const url = `${API_BASE}/admin/birthdays/logs${qs ? '?' + qs : ''}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Failed to fetch birthday logs: ${error}`);
      }
      return res.json() as Promise<BirthdayTicketLog[]>;
    }
  });
}

export function useUploadBirthdays() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const token = sessionStorage.getItem('hmh_access_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/admin/birthdays/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Upload failed: ${error}`);
      }
      return res.json() as Promise<UploadSummary>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['birthday-employees'] });
    }
  });
}

export function useProcessTodaysBirthdays() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_BASE}/admin/birthdays/process-today`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Processing failed: ${error}`);
      }
      return res.json() as Promise<ProcessSummary>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['birthday-logs'] });
    }
  });
}

export function useResendBirthdayEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (logId: number) => {
      const res = await fetch(`${API_BASE}/admin/birthdays/logs/${logId}/resend`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Resend failed: ${error}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['birthday-logs'] });
    }
  });
}

export function useUpdateBirthdayEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, email }: { id: number; email: string }) => {
      const res = await fetch(`${API_BASE}/admin/birthdays/${id}/email`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email })
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Update failed: ${error}`);
      }
      return res.json() as Promise<BirthdayEmployee>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['birthday-employees'] });
    }
  });
}

export function useDeactivateBirthdayEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_BASE}/admin/birthdays/${id}/deactivate`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(`Deactivation failed: ${error}`);
      }
      return res.json() as Promise<BirthdayEmployee>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['birthday-employees'] });
    }
  });
}
